import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { Booking } from './Booking'
import { ComputeFactory } from './ComputeFactory'
import { Participant, ParticipantConstructorArgs } from './Participant'
import { FKType } from './SQLBuilder'
import { User } from './User'

type ConferenceConstructorArgs = Omit<ConstructorArgs<() => Conference>, 'participants'> & {
    participants: Participant[] | ParticipantConstructorArgs[] | null
}

export class Conference extends BaseRecord {
    readonly recordType = 'conference'

    refnum?: string
    duration?: number // Integer
    cancelledDate?: moment.Moment
    completedDate?: moment.Moment
    lastModifiedDate?: moment.Moment

    readonly participants: Participant[] = []
    readonly firstPro?: User

    static SHOW_CONFERENCE_LIMIT_MINUTES = 30
    static START_CONFERENCE_LIMIT_MINUTES = 15

    constructor(data?: Partial<ConferenceConstructorArgs>) {
        super(data)
        if (!data) {
            this.owner = PSApplication.owner()
            return
        }

        this.refnum = data.refnum ?? undefined
        this.duration = this.propertyFromIntArg(data.duration)
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.completedDate = this.propertyFromTimestampArg(data.completed_date)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)

        this.participants = (data.participants ?? [])
            .map((participant) =>
                participant instanceof Participant ? participant : new Participant(participant)
            )
            .filter((participant) => !participant.completedDate)

        this.firstPro = (() => {
            const client = this.booking?.client
            for (const participant of this.participants) {
                if (
                    !participant.completedDate &&
                    participant.person &&
                    participant.person !== client
                )
                    return participant.person
            }

            return this.booking?.owner
        })()
    }

    clone(overrides?: Partial<ConferenceConstructorArgs>): Conference {
        return new Conference({ ...this.serializeForClone(), ...overrides })
    }

    static async allowJoin(booking: Booking, me: User): Promise<boolean> {
        if (booking.allowConference && booking.confirmedDate && !booking.cancelledDate) {
            const date = moment()
            const owner = booking.owner
            const allowFeature = !!owner?.subscribedDate
            const allowUser =
                me === booking.client ||
                me === owner ||
                (me.perm === User.PERM_ROLE_ADMIN && me.owner === owner) ||
                (await ComputeFactory.getInstance().fetchJobs(me, booking, false)).length > 0
            return (
                allowFeature &&
                allowUser &&
                date.isAfter(
                    moment(booking.date).subtract(
                        Conference.SHOW_CONFERENCE_LIMIT_MINUTES,
                        'minutes'
                    )
                ) &&
                date.isBefore(
                    moment(booking.endDate).add(
                        Conference.START_CONFERENCE_LIMIT_MINUTES,
                        'minutes'
                    )
                )
            )
        }
        return false
    }

    // todo: implement ComputeFactory.fetchJobs
    // static allowStart(booking: Booking, me: User): boolean {
    //     if (booking.allowConference && booking.confirmedDate && !booking.cancelledDate) {
    //         const date = moment()
    //         const owner = booking.owner
    //         const allowFeature = !!owner?.subscribedDate
    //         const allowUser = me === booking.client || me === owner || (me.perm === User.PERM_ROLE_ADMIN && me.owner === owner)
    //             || ComputeFactory.fetchJobs(me, booking, false).length > 0
    //         return allowFeature && allowUser && date.isAfter(moment(booking.date).subtract(Conference.START_CONFERENCE_LIMIT_MINUTES, 'minutes')) && date.isBefore(moment(booking.endDate).add(Conference.START_CONFERENCE_LIMIT_MINUTES, 'minutes'))
    //     }
    //     return false
    // }

    deserialize(data: any) {
        super.deserialize(data)
        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.creator) this.creator = BaseRecord.fromSerializer(data.creator, User)
        if (data.booking) this.booking = BaseRecord.fromSerializer(data.booking, Booking)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data.completedDate) this.completedDate = moment(data.completedDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
    }

    static dataType(): DataType {
        return 'conferences'
    }

    static fk: FKType = {
        users: ['owner', 'creator'],
        bookings: ['booking'],
    }

    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get creator(): User | undefined {
        return this.getForeignRecord('creator', User)
    }
    set creator(creator: User | undefined) {
        this.setForeignRecord('creator', creator)
    }
    get booking(): Booking | undefined {
        return this.getForeignRecord('booking', Booking)
    }
    set booking(booking: Booking | undefined) {
        this.setForeignRecord('booking', booking)
    }
}
